<script lang="ts" context="module">
  import debounce from "lodash.debounce";
  import { onMount } from "svelte";
  import TOCContent from "./TOCContent.svelte";
  import type { TableOfContentItem } from "./types";
</script>

<script lang="ts">
  export let showEmpty: boolean = false;

  let tocItems: TableOfContentItem[] = [];
  let showMobileTOC: boolean = false;
  let headerTop: number = 0;
  let headerLeft: number = 0;
  let showScrollProgressIndicator: boolean = false;
  let positionCalculated: boolean = false;
  let headerElement: HTMLElement | null = null;

  const debounceCalculateMobileTriggerButtonLocation = debounce(calculateMobileTriggerButtonLocation, 200);

  onMount(() => {
    headerElement = (document.querySelector("[slot=header]") as HTMLElement).parentElement!;
    const elements = [...document.querySelectorAll(".author-show [data-block-toc=true], .container-show [data-block-toc=true]")];
    tocItems = elements.map((element) => {
      const htmlElement = element as HTMLElement;
      return { caption: htmlElement.dataset.tocText!, id: htmlElement.dataset.blockId! };
    });

    decideWhetherToShowScrollProgressIndicator();

    debounceCalculateMobileTriggerButtonLocation();
  });

  function decideWhetherToShowScrollProgressIndicator() {
    const newsletterElement = document.querySelector(".container-show")!;
    if (newsletterElement) {
      const newsletterHeight = newsletterElement.getBoundingClientRect().height;
      showScrollProgressIndicator = newsletterHeight > window.innerHeight * 2.5;
    }
  }

  function calculateMobileTriggerButtonLocation() {
    if (!headerElement) {
      return;
    }
    const contentRect = headerElement.getBoundingClientRect();
    headerTop = headerElement.offsetTop || contentRect.top;
    headerLeft = headerElement.offsetLeft || contentRect.left;
    headerTop -= 20;
    headerLeft -= 20;
    if (headerLeft < 5) {
      headerLeft = 5;
    }
    setTimeout(() => {
      positionCalculated = true;
    }, 500);
  }

  function toggleMobileTOC(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    showMobileTOC = !showMobileTOC;
    calculateMobileTriggerButtonLocation();
  }
</script>

<svelte:window on:resize={calculateMobileTriggerButtonLocation} on:scroll={debounceCalculateMobileTriggerButtonLocation} />

{#if tocItems.length > 0 || showEmpty}
  <div
    class="items-top hide-menu fixed z-[155] hidden"
    class:mobile-toc-wrapper={positionCalculated}
    style:--left={headerLeft + "px"}
    class:bottom-0={showMobileTOC}
    style:top={showMobileTOC ? "0" : headerTop + "px"}
    style:left={showMobileTOC ? "0" : headerLeft + "px"}
    class:show-menu={showMobileTOC}>
    <button
      class="material-icons z-[155] flex size-10 items-center justify-center rounded-full bg-white text-nxgray-400 shadow-md"
      style:top={showMobileTOC ? headerTop + "px" : "0"}
      style:left={showMobileTOC ? headerLeft + "px" : "0"}
      on:click={toggleMobileTOC}
      aria-label={showMobileTOC ? "Close table of contents" : "Open table of contents"}>
      {#if showMobileTOC}
        close
      {:else}
        toc
      {/if}
    </button>
    <div class="bottom-0 left-0 top-0 h-full bg-white px-2 py-2.5 shadow-md" class:hidden={!showMobileTOC}>
      <div class="sticky top-0">
        <TOCContent {tocItems} {showScrollProgressIndicator} />
      </div>
    </div>
  </div>
  <div
    class="fixed hidden text-13 shadow-lg backdrop-blur-md backdrop-filter"
    style:top={headerTop + 20 + "px"}
    class:toc-container={positionCalculated}>
    <TOCContent {tocItems} {showScrollProgressIndicator} />
  </div>
{/if}

<style>
  :global(.show-new .toc-container) {
    left: 0;
  }

  @media (max-width: 1149px) {
    :global(.show-new .mobile-toc-wrapper) {
      display: flex;
    }
  }

  :global(.show-new .toc-container) {
    display: none;
  }

  @media (min-width: 1150px) {
    :global(.show-new .toc-container) {
      display: block;
    }
  }

  @media (min-width: 1280px) {
    :global(.show-new .toc-container) {
      display: block;
      left: auto;
      right: var(--right, calc(50vw + 400px));
    }
  }

  @media (min-width: 1536px) {
    :global(.show_author_new .toc-container) {
      display: block;
    }
    .toc-container {
      display: block;
      left: auto;
      right: var(--right, calc(50vw + 400px));
    }
  }

  .show-menu > div {
    animation: showMe 0.5s ease-in-out forwards;
  }

  @keyframes showMe {
    from {
      opacity: 0;
      transform: translateX(-100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .show-menu > button {
    position: absolute;
    animation: moveButton 0.5s ease-in-out forwards;
  }

  @keyframes moveButton {
    from {
      left: var(--left, 0);
    }
    to {
      left: calc(100% - 20px);
    }
  }
</style>
